import { ErrorMessageContents } from '../../helpers/hub'
import { OrderEntryModel } from '../depthOfMarket/types'
import { StagedOrder } from '../stagedOrders/types'
import { Order, OrderType, TobParameters, ValidationResult } from './types'

export const setMyOrdersOpen = (gridIndex: number, open: boolean) =>
  ({ type: 'order.setMyOrdersOpen', payload: { gridIndex, open } } as const)
export type SetMyOrdersOpenAction = ReturnType<typeof setMyOrdersOpen>

export const updateOrderValidation = (validationResult: ValidationResult) =>
  ({ type: 'order.updateValidation', payload: validationResult } as const)
export type UpdateOrderValidationAction = ReturnType<
  typeof updateOrderValidation
>

export const updateOrdersValidations = (
  validationResults: ValidationResult[]
) => ({ type: 'order.updateValidations', payload: validationResults } as const)
export type UpdateOrdersValidationsAction = ReturnType<
  typeof updateOrdersValidations
>

export const resetOrdersValidation = () =>
  ({ type: 'order.resetValidations' } as const)
export type ResetOrdersValidationAction = ReturnType<
  typeof resetOrdersValidation
>

export const submitOrder = (
  order: Order,
  orderId: string,
  size: number,
  transactionId: number,
  custId: number,
  spotCrossSelection: string,
  listId?: number
) =>
  ({
    type: 'order.submit',
    payload: {
      order,
      orderId,
      size,
      transactionId,
      custId,
      spotCrossSelection,
      listId
    } as const
  } as const)
export type SubmitOrderAction = ReturnType<typeof submitOrder>

export const createTempOrder = (
  transactionId: number,
  securityId: number,
  type: OrderType,
  price: number,
  isSpread: boolean,
  size: number,
  allOrNone: boolean,
  individualMin: number,
  tob: TobParameters,
  initialOrder?: Order,
  totalSize?: number
) =>
  ({
    type: 'order.createTempOrder',
    payload: {
      transactionId,
      securityId,
      type,
      price,
      isSpread,
      size,
      initialOrder,
      allOrNone,
      individualMin,
      tob,
      totalSize
    }
  } as const)
export type CreateTempOrderAction = ReturnType<typeof createTempOrder>

export const createTempAggressorOrder = (
  initialOrder: Order,
  transactionId: number
) =>
  ({
    type: 'order.createTempAggressorOrder',
    payload: { initialOrder, transactionId }
  } as const)
export type CreateTempAggressorOrderAction = ReturnType<
  typeof createTempAggressorOrder
>

export const cancelOrder = (orderId: string, updateStagedOrder = true) =>
  ({ type: 'order.cancel', payload: { orderId, updateStagedOrder } } as const)
export type CancelOrderAction = ReturnType<typeof cancelOrder>

export const cancelOrders = (orderIds: string[], updateStagedOrder = true) =>
  ({
    type: 'order.cancelOrders',
    payload: { orderIds, updateStagedOrder }
  } as const)
export type CancelOrdersAction = ReturnType<typeof cancelOrders>

export const cancelOrderFailure = (
  orderId: string,
  error: ErrorMessageContents
) =>
  ({
    type: 'order.cancelFailure',
    error: true,
    payload: { orderId, error }
  } as const)
export type CancelOrderActionFailure = ReturnType<typeof cancelOrderFailure>

export const submitOrderFailure = (
  orderId: string,
  transactionId: number,
  listId: number,
  error: ErrorMessageContents,
  size: number
) =>
  ({
    type: 'order.submitFailure',
    error: true,
    payload: { orderId, transactionId, listId, error, size } as const
  } as const)
export type SubmitOrderFailureAction = ReturnType<typeof submitOrderFailure>

export const createOrders = (params: StagedOrder[]) =>
  ({ type: 'order.createOrders', payload: { params } } as const)
export type CreateOrdersAction = ReturnType<typeof createOrders>

export const createOrder = (securityId: number, order: OrderEntryModel) =>
  ({ type: 'order.createOrder', payload: { securityId, order } } as const)
export type CreateOrderAction = ReturnType<typeof createOrder>

export const fetchUserOrders = () =>
  ({ type: 'order.fetchUserOrders' } as const)
export type FetchUserOrdersAction = ReturnType<typeof fetchUserOrders>

export const fetchOperatorOrders = () =>
  ({ type: 'order.fetchOperatorOrders' } as const)
export type FetchOperatorOrdersAction = ReturnType<typeof fetchOperatorOrders>

export const unsubscribeFetchUserOrders = () =>
  ({ type: 'order.unsubscribeFetchUserOrders' } as const)
export type UnsubscribeFetchUserOrdersAction = ReturnType<
  typeof unsubscribeFetchUserOrders
>

export const fetchResubmitOrders = () =>
  ({ type: 'order.fetchResubmitOrders' } as const)
export type FetchResubmitOrdersAction = ReturnType<typeof fetchResubmitOrders>

export const loadResubmitOrders = (resubmitOrders: any) =>
  ({ type: 'order.loadResubmitOrders', payload: resubmitOrders } as const)
export type LoadResubmitOrdersAction = ReturnType<typeof loadResubmitOrders>

export const addOrUpdateUserOrders = (orders: Order[]) =>
  ({ type: 'order.addOrUpdateUserOrders', payload: orders } as const)
export type AddOrUpdateUserOrdersAction = ReturnType<
  typeof addOrUpdateUserOrders
>

export const addOrUpdateOperatorOrders = (orders: Order[]) =>
  ({ type: 'order.addOrUpdateOperatorOrders', payload: orders } as const)
export type AddOrUpdateOperatorOrdersAction = ReturnType<
  typeof addOrUpdateOperatorOrders
>

export const setOrderError = (orderId: Order['id']) =>
  ({ type: 'order.setOrderError', payload: orderId } as const)
export type SetOrderErrorAction = ReturnType<typeof setOrderError>

export const handleErr = (
  err: any,
  securityId: number,
  orderType: OrderType,
  isin?: ValidationResult['isin']
) =>
  ({
    type: 'order.createOrderError',
    payload: { err, securityId, orderType, isin }
  } as const)
export type CreateOrderErrorAction = ReturnType<typeof handleErr>

export type CreateOrderBulkErrorValue = {
  error: CreateOrderErrorAction & { isin?: ValidationResult['isin'] }
  date: Date
}

export const handleBulkErr = (error: CreateOrderBulkErrorValue[]) =>
  ({ type: 'order.createOrderBulkError', payload: { error } } as const)
export type CreateOrderErrorBulkAction = ReturnType<typeof handleBulkErr>

export const mapFakeTransactionId = (
  fakeTransactionId: number,
  transactionId: number
) =>
  ({
    type: 'order.mapFakeTransactionId',
    payload: { fakeTransactionId, transactionId }
  } as const)
export type MapFakeTransactionIdAction = ReturnType<typeof mapFakeTransactionId>

export const setValidation = (securityId: number, orderType: OrderType) =>
  ({ type: 'order.setValidation', payload: { securityId, orderType } } as const)
export type SetValidationAction = ReturnType<typeof setValidation>

export const setValidationOpen = (validationOpen: boolean) =>
  ({ type: 'order.setValidationOpen', payload: { validationOpen } } as const)
export type SetValidationOpenAction = ReturnType<typeof setValidationOpen>

export const clearMyOrders = () => ({ type: 'order.clearMyOrders' } as const)
export type ClearMyOrdersAction = ReturnType<typeof clearMyOrders>

export const ordersUpdated = (order: Order) =>
  ({ type: 'order.ordersUpdated', payload: order } as const)
export type OrdersUpdatedAction = ReturnType<typeof ordersUpdated>

export const setTradeConfirmId = (tradeConfirmId: number | null) =>
  ({ type: 'order.setTradeConfirmId', payload: { tradeConfirmId } } as const)
export type SetTradeConfirmIdAction = ReturnType<typeof setTradeConfirmId>

export type OrderAction =
  | SetMyOrdersOpenAction
  | UpdateOrderValidationAction
  | UpdateOrdersValidationsAction
  | ResetOrdersValidationAction
  | SubmitOrderAction
  | SubmitOrderFailureAction
  | CancelOrderAction
  | CancelOrderActionFailure
  | FetchUserOrdersAction
  | UnsubscribeFetchUserOrdersAction
  | AddOrUpdateUserOrdersAction
  | AddOrUpdateOperatorOrdersAction
  | CreateTempOrderAction
  | CreateTempAggressorOrderAction
  | SetOrderErrorAction
  | MapFakeTransactionIdAction
  | CreateOrderErrorAction
  | CreateOrderErrorBulkAction
  | SetValidationAction
  | SetValidationOpenAction
  | FetchResubmitOrdersAction
  | LoadResubmitOrdersAction
  | ClearMyOrdersAction
  | OrdersUpdatedAction
  | SetTradeConfirmIdAction
  | FetchOperatorOrdersAction
